import React, { useState } from "react";
import { View, TextInput, StyleSheet, Platform } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Skeleton } from "@/components/ui/Skeleton";
import { CallButton } from "@/components/ui/CallButton";
import { formatPhoneNumber, validatePhoneNumber } from "@/utils/phoneValidation";

interface PhoneInputProps {
  label?: string;
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  required?: boolean;
  editable?: boolean;
  /** Show skeleton loading state */
  loading?: boolean;
}

export function PhoneInput({
  label,
  value,
  onChangeText,
  placeholder = "(555) 555-5555",
  required = false,
  editable = true,
  loading = false,
}: PhoneInputProps) {
  const { colors } = useTheme();
  const [isFocused, setIsFocused] = useState(false);

  const isValidFormat = value.length === 0 || validatePhoneNumber(value);
  const hasError = value.length > 0 && !isValidFormat;

  // Handle text change with formatting
  const handleChangeText = (text: string) => {
    const formatted = formatPhoneNumber(text.trim());
    onChangeText(formatted);
  };

  // Loading state - show skeleton
  if (loading) {
    return (
      <View style={styles.container}>
        {label && <ThemedText style={styles.label}>{label}</ThemedText>}
        <Skeleton width="100%" height={44} radius={8} />
      </View>
    );
  }

  // Readonly state - show phone number with call button
  if (!editable) {
    return (
      <View style={styles.container}>
        {label && <ThemedText style={styles.label}>{label}</ThemedText>}
        <View
          style={[styles.inputContainer, { borderColor: colors.border, backgroundColor: colors.backgroundSecondary }]}
        >
          <MaterialCommunityIcons name="phone-outline" size={18} color={colors.textSecondary} style={styles.icon} />
          {value ? (
            <>
              <ThemedText style={[styles.phoneText, { color: colors.text, flex: 1 }]}>{value}</ThemedText>
              <CallButton phoneNumber={value} size={32} />
            </>
          ) : (
            <ThemedText style={[styles.phoneText, { color: colors.placeholder }]}>—</ThemedText>
          )}
        </View>
      </View>
    );
  }

  const borderColor = hasError ? colors.ruby?.border || "red" : isFocused ? colors.primary : colors.border;

  return (
    <View style={styles.container}>
      {(label || hasError) && (
        <View style={styles.labelRow}>
          {label && (
            <ThemedText style={styles.label}>
              {label}
              {required && <ThemedText style={{ color: colors.ruby?.text || "red" }}> *</ThemedText>}
            </ThemedText>
          )}
          {hasError && (
            <ThemedText style={[styles.errorText, { color: colors.dangerText || colors.ruby?.text || "red" }]}>
              Invalid phone number
            </ThemedText>
          )}
        </View>
      )}
      <View style={[styles.inputContainer, { borderColor, backgroundColor: colors.backgroundSecondary }]}>
        <MaterialCommunityIcons
          name="phone-outline"
          size={18}
          color={hasError ? colors.ruby?.text || "red" : isFocused ? colors.primary : colors.textSecondary}
          style={styles.icon}
        />
        <TextInput
          value={value}
          onChangeText={handleChangeText}
          placeholder={placeholder}
          placeholderTextColor={colors.placeholder}
          style={[styles.input, { color: colors.text }]}
          keyboardType="phone-pad"
          autoCapitalize="none"
          autoCorrect={false}
          autoComplete="tel"
          editable={editable}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
        {value.length > 0 && isValidFormat && (
          <MaterialCommunityIcons name="check-circle" size={18} color={colors.emerald?.text || "green"} />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 4,
  },
  labelRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: "600",
  },
  inputContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    height: 44,
  },
  icon: {
    marginRight: 8,
  },
  input: {
    flex: 1,
    fontSize: 15,
    height: "100%",
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
  },
  phoneText: {
    fontSize: 15,
  },
  errorText: {
    fontSize: 12,
  },
});
